<script setup lang="ts">
/**
 * Generic BFF 401 error page for codes that carry a backend error message.
 *
 * Accepts a `code` prop so the same component handles different BFF auth
 * error codes (e.g. `BFF_SESSION_EXPIRED`, `BFF_CSRF_MISMATCH`). The
 * explanation is enriched with the backend message from `route.query.errorMessage`
 * via `errorExplanationFromBackend`. Actions fall back to
 * `DEFAULT_AUTH_ERROR_ACTIONS` when the entry has no custom list. Shows
 * a "Go To Login" button.
 */
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import ErrorView from '@/components/errors/ErrorView.vue'
import { redirectToLogin } from '@/api/keycloak_api'
import { ErrorCode } from '@/errors/codes'
import {
  DEFAULT_AUTH_ERROR_ACTIONS,
  ERROR_USER_MESSAGES,
  errorExplanationFromBackend,
} from '@/errors/error_user_messages'

const props = defineProps<{
  code: ErrorCode
}>()

const route = useRoute()

const title = computed(() => ERROR_USER_MESSAGES[props.code].title)
const explanation = computed(() =>
  errorExplanationFromBackend(props.code, route.query.errorMessage),
)

const actions = computed(
  () => ERROR_USER_MESSAGES[props.code].actions ?? DEFAULT_AUTH_ERROR_ACTIONS,
)

function goToLogin() {
  redirectToLogin()
}
</script>

<template>
  <ErrorView :code="props.code" :title="title" :explanation="explanation" :actions="actions">
    <button class="tufte-button" @click="goToLogin">Go To Login</button>
  </ErrorView>
</template>
